// When two bills are "the same period". Shared by the server (the parser's
// period correction) and the browser (creating a bill by hand), so both refuse
// the same duplicates.
export type PeriodLike = { id: string; bill_from: string; bill_to: string };

/**
 * The other bill that already holds this period, if any. "Holds" means the
 * overlap covers more than half of the shorter of the two periods, so that a
 * retailer's boundary shift of a few days still counts as the same period,
 * while a neighbouring month that shares a day or two does not.
 * Pass the site's active bills only.
 */
export function periodTakenBy<T extends PeriodLike>(bills: T[], selfId: string, from: string, to: string): T | null {
  const day = (d: string) => Math.round(new Date(d).getTime() / 86_400_000);
  const [f, t] = [day(from), day(to)];
  for (const b of bills) {
    if (b.id === selfId || !b.bill_from || !b.bill_to) continue;
    const [bf, bt] = [day(b.bill_from), day(b.bill_to)];
    const overlap = Math.min(t, bt) - Math.max(f, bf) + 1;
    const shorter = Math.min(t - f, bt - bf) + 1;
    if (overlap > 0 && overlap * 2 > shorter) return b;
  }
  return null;
}

const auDate = (d: string) => new Date(d).toLocaleDateString('en-AU', { timeZone: 'UTC' });

/** What to tell someone who tried to create a second bill for a held period. */
export function periodTakenMessage(taken: PeriodLike): string {
  return `A bill for ${auDate(taken.bill_from)} – ${auDate(taken.bill_to)} already exists for this site. `
    + 'Open that bill and add the invoice or usage there instead of creating a second bill for the same period.';
}
